import React, { memo, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { List, fromJS } from 'immutable';
import { push } from 'connected-react18-router';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import {
  loadDirectors,
  loadDirectorTeams,
  loadUserTitles,
  createTeam,
  loadUsers,
  addUsersToTeam,
  deleteUserFromTeam,
  deleteTeam,
  updateUserTeam,
} from '../../actions/directorDashboard';
import config from '../../config';
import { showError } from '../../utils/MessagePopup';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { ModuleSettingsTable, AddTeam, DirectorsSidebar } from '../../components/DirectorDashboard/ModuleSettingsTable';
import { usePopupContext } from '../../contexts/PopupContext';
import Button from '../../components/helpers/Button';

const ModuleSettingsContainer = ({
  push,
  user,
  users,
  directors,
  directorsLoading,
  directorTeams,
  directorTeamsLoading,
  userTitles,
  loadDirectors,
  loadDirectorTeams,
  loadUserTitles,
  loadUsers,
  addUsersToTeam,
  deleteUserFromTeam,
  createTeam,
  updateUserTeam,
  deleteTeam,
}) => {
  const { openPopup, closePopup } = usePopupContext();
  const params = useParams();
  const [isAddTeamState, setAddTeamState] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [directorsList, setDirectorsList] = useState(List());
  const [usersList, setUsersList] = useState(List());
  const [pendingUsers, setPendingUsers] = useState(List());
  const [pendingDirectReports, setPendingDirectReports] = useState(List());

  useEffect(() => {
    loadDirectors();
    loadUserTitles();
  }, []);

  useEffect(() => {
    setDirectorsList(directors);
  }, [directors]);

  useEffect(() => {
    const { id } = params;

    if (id) {
      loadDirectorTeams(id);
      loadUsers(id);
    }
  }, [params.id]);

  // Fill user list from directorTeams.
  useEffect(() => {
    if (!users) {
      setUsersList(List());

      return;
    }

    if (!directorTeams) {
      setUsersList(users);

      return;
    }

    const usedIds = new Set();

    const directorId = directorTeams.getIn(['director', 'id']);

    if (directorId) {
      usedIds.add(directorId);
    }

    directorTeams.get('directReports', List()).forEach(report => {
      usedIds.add(report.get('id'));
    });

    directorTeams.get('teams', List()).forEach(team => {
      team.get('users', List()).forEach(user => {
        usedIds.add(user.get('id'));
      });
    });

    setUsersList(users.filter(user => !usedIds.has(user.get('id'))));
  }, [directorTeams, users]);

  const userRole = useMemo(() => user.getIn(['roles', 0, 'slug'], null), [user]);
  const isSuperUser = useMemo(() => userRole === 'superuser', [userRole]);

  useEffect(() => {
    if (!isSuperUser) {
      showError(openPopup, config.permisionError, () => {
        push('/');
        closePopup();
      });
    }
  }, [isSuperUser, openPopup, closePopup, push]);

  const handleSaveClick = () => {
    setAddTeamState(false);
    setTeamError(null);

    const allPending = pendingUsers.concat(pendingDirectReports);

    if (allPending.size > 0) {
      addUsersToTeam({
        body: {
          users: allPending
            .map(pendingUser => ({
              userId: pendingUser.get('userId'),
              teamId: pendingUser.get('teamId'),
              directorDashboardUserTitleId: pendingUser.get('directorDashboardUserTitleId'),
            }))
            .toJS(),
        },
        directorUserId: params.id,
        afterSuccess: () => {
          loadDirectorTeams(params.id);
          loadUsers(params.id);
          setPendingUsers(List());
          setPendingDirectReports(List());
        },
      });
    }
  };

  const handleUserRemoveClick = membershipId => {
    const pendingIndex = pendingUsers.findIndex(pu => pu.get('userId') === membershipId);

    if (pendingIndex !== -1) {
      setPendingUsers(prev => prev.delete(pendingIndex));

      const originalUser = users?.find(userItem => userItem.get('id') === membershipId);

      if (originalUser) {
        setUsersList(prev => prev.push(originalUser));
      }

      return;
    }

    const pendingDirectIndex = pendingDirectReports.findIndex(pu => pu.get('userId') === membershipId);

    if (pendingDirectIndex !== -1) {
      setPendingDirectReports(prev => prev.delete(pendingDirectIndex));

      const originalUser = users?.find(userItem => userItem.get('id') === membershipId);

      if (originalUser) {
        setUsersList(prev => prev.push(originalUser));
      }

      return;
    }

    openPopup('ConfirmPopup', {
      header: 'Delete user',
      message: 'Are you sure you want to delete this user?',
      yes: 'Confirm',
      no: 'Cancel',
      onOk: () => {
        deleteUserFromTeam({
          directorUserId: params.id,
          membershipId,
          afterSuccess: () => {
            loadDirectorTeams(params.id);
            loadUsers(params.id);
            closePopup();
          },
          afterError: () => {
            closePopup();
          },
        });
      },
      onCancel: () => {
        closePopup();
      },
    });
  };

  const handleTeamRemoveClick = teamId => {
    openPopup('ConfirmPopup', {
      header: 'Delete team',
      message: 'Are you sure you want to delete this team?',
      yes: 'Confirm',
      no: 'Cancel',
      onOk: () => {
        deleteTeam({
          directorUserId: params.id,
          teamId,
          afterSuccess: () => {
            loadDirectorTeams(params.id);
            loadUsers(params.id);
            setPendingUsers(List());
            closePopup();
          },
          afterError: () => {
            closePopup();
          },
        });
      },
      onCancel: () => {
        closePopup();
      },
    });
  };

  const handleRoleChange = (event, user) => {
    updateUserTeam({
      directorUserId: params.id,
      membershipId: user.get('membershipId'),
      body: {
        directorDashboardUserTitleId: event.target.value,
      },
    });
  };

  const handleTeamCreate = event => {
    event.preventDefault();

    const form = event.currentTarget;
    const leadUserId = form.leadUserId.value;
    const name = form.name.value;
    const directorDashboardUserTitleId = form.directorDashboardUserTitleId.value;

    if (!leadUserId || !name || !directorDashboardUserTitleId) {
      setTeamError('Pick a lead, name the team, and choose the lead’s role.');

      return;
    }

    setAddTeamState(false);
    setTeamError(null);
    createTeam({
      directorUserId: params.id,
      body: { leadUserId, name, directorDashboardUserTitleId },
      afterSuccess: () => {
        loadDirectorTeams(params.id);
        loadUsers(params.id);
      },
    });
  };

  const handleTeamCancel = () => {
    setAddTeamState(false);
    setTeamError(null);
  };

  const handleShowAddTeamWidgetClick = () => {
    setAddTeamState(true);
  };

  const handleUserAddClick = (user, teamId) => {
    const newPendingUser = fromJS({
      userId: user.get('id'),
      teamId,
      directorDashboardUserTitleId: userTitles?.first()?.get('id'),
      firstName: user.get('firstName'),
      lastName: user.get('lastName'),
    });

    if (teamId === null) {
      setPendingDirectReports(prev => prev.push(newPendingUser));
    } else {
      setPendingUsers(prev => prev.push(newPendingUser));
    }
    setUsersList(prev => prev.filter(userItem => userItem.get('id') !== user.get('id')));
  };

  const handlePendingUserRoleChange = (event, userId) => {
    setPendingUsers(prev => {
      const index = prev.findIndex(pendingUser => pendingUser.get('userId') === userId);

      if (index === -1) return prev;

      return prev.setIn([index, 'directorDashboardUserTitleId'], Number(event.target.value));
    });
    setPendingDirectReports(prev => {
      const index = prev.findIndex(pendingUser => pendingUser.get('userId') === userId);

      if (index === -1) return prev;

      return prev.setIn([index, 'directorDashboardUserTitleId'], Number(event.target.value));
    });
  };

  const handleSearch = event => {
    const searchDirector = event.target.value.trim();

    if (!searchDirector) {
      setDirectorsList(directors);

      return;
    }

    const search = searchDirector.toLowerCase();
    const foundDirectors = directors.filter(
      director =>
        director
          .get('firstName', '')
          .toLowerCase()
          .includes(search) ||
        director
          .get('lastName', '')
          .toLowerCase()
          .includes(search),
    );

    setDirectorsList(foundDirectors);
  };

  const handleSearchClear = () => {
    setDirectorsList(directors);
  };

  const handleSelectDirector = directorUserId => {
    if (!hasUnsavedUsers && !isAddTeamState) {
      push(`/director-dashboard/module-settings/${directorUserId}`);

      return;
    }

    openPopup('ConfirmPopup', {
      header: 'Unsaved changes',
      message: 'Are you sure you want to change the director?',
      yes: 'Confirm',
      no: 'Cancel',
      onOk: () => {
        setTeamError(null);
        setAddTeamState(false);
        setPendingUsers(List());
        setPendingDirectReports(List());
        push(`/director-dashboard/module-settings/${directorUserId}`);
        closePopup();
      },
      onCancel: () => {
        closePopup();
      },
    });
  };

  const hasUnsavedUsers = useMemo(() => pendingUsers.size > 0 || pendingDirectReports.size > 0, [
    pendingUsers,
    pendingDirectReports,
  ]);

  const buttons = useMemo(() => {
    if (!params.id) {
      return null;
    }

    return (
      <div className="col-md-6 text-right">
        <Button className="btn-primary ml5" disabled={!hasUnsavedUsers} onClick={handleSaveClick}>
          Save
        </Button>
        <Button className="btn-primary ml5 mr15" onClick={handleShowAddTeamWidgetClick}>
          + Add Team
        </Button>
      </div>
    );
  }, [params.id, handleSaveClick, hasUnsavedUsers]);

  if (!isSuperUser) return null;

  return (
    <>
      <div className="row d-flex align-items-end pt10">
        <div className="col-md-12">
          <h3 className="text-primary-dark text-bold">Module Settings</h3>
          <h6>
            {isSuperUser
              ? 'Select a Director on the left. Configure their teams and direct reports the Statistics and Benchmarks tabs read from whatever is saved here.'
              : "Add teams, assign people from the firm directory, and set each person's role. The Statistics and Benchmarks tabs read from whatever is saved here."}
          </h6>
        </div>
        {buttons}
      </div>
      <div className="row mt20">
        {isSuperUser && (
          <div className="col-sm-3 col-md-4 col-lg-3 col-xxl-2">
            <DirectorsSidebar
              count={directors?.size || 0}
              data={directorsList}
              isLoading={directorsLoading}
              onSearch={handleSearch}
              onSearchClear={handleSearchClear}
              onSelect={handleSelectDirector}
            />
          </div>
        )}
        {params.id && (
          <div
            className={classNames({
              'col-sm-9 col-md-8 col-lg-9 col-xxl-10': isSuperUser,
              'col-sm-12 col-md-12 col-lg-12 col-xxl-12': !isSuperUser,
            })}
          >
            {isAddTeamState && (
              <AddTeam
                error={teamError}
                onCancel={handleTeamCancel}
                onSubmit={handleTeamCreate}
                roles={userTitles}
                users={usersList}
              />
            )}
            <ModuleSettingsTable
              allUsers={users}
              availableUsers={usersList}
              directors={directorTeams}
              isLoading={directorTeamsLoading}
              onPendingUserRoleChange={handlePendingUserRoleChange}
              onRoleChange={handleRoleChange}
              onTeamRemove={handleTeamRemoveClick}
              onUserAdd={handleUserAddClick}
              onUserRemove={handleUserRemoveClick}
              pendingDirectReports={pendingDirectReports}
              pendingUsers={pendingUsers}
              roles={userTitles}
            />
          </div>
        )}
      </div>
    </>
  );
};

function mapStateToProps(state) {
  return {
    directors: state.directorDashboard.get('directors'),
    directorsLoading: state.directorDashboard.get('directorsLoading'),
    directorTeams: state.directorDashboard.get('directorTeams'),
    directorTeamsLoading: state.directorDashboard.get('directorTeamsLoading'),
    userTitles: state.directorDashboard.get('userTitles'),
    users: state.directorDashboard.get('users'),
    user: state.auth.get('user', null),
  };
}

export { ModuleSettingsContainer };
export default connect(
  mapStateToProps,
  {
    push,
    loadDirectors,
    loadDirectorTeams,
    loadUserTitles,
    createTeam,
    loadUsers,
    addUsersToTeam,
    deleteUserFromTeam,
    updateUserTeam,
    deleteTeam,
  },
  mergeProps,
  connectOptions,
)(memo(ModuleSettingsContainer));
